import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { RadioGroup } from '@sweetspot/scramble-ds/atoms'
import { useBallPricingContext } from '@sweetspot/club-portal-legacy/pages/Settings/components/BallPricing/BallPricingProvider'
import { cn } from '@sweetspot/scramble-ds/utils'
import { Input, InputBase, InputContainer } from '@sweetspot/scramble-ds'

const BallDispenseFlowForm = () => {
  const { t } = useTranslation()

  const {
    bucketConfiguration: {
      maxNrOfBalls,
      isSplitUpDispense,
      setIsSplitUpDispense,
      ballsPerDispense,
      setBallsPerDispense,
    },
  } = useBallPricingContext()

  const preventedSymbols = useMemo(() => ['e', 'E', '+', '-', ',', '.'], [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const isPreventedSymbol = preventedSymbols.includes(e.key)
      const value = (e.target as HTMLInputElement).value
      const hasTwoOrMoreDecimals =
        !['Backspace', 'Delete'].includes(e.key) && !e.metaKey && value.split('.')[1]?.length >= 2

      if (isPreventedSymbol || hasTwoOrMoreDecimals) {
        e.preventDefault()
      }
    },
    [preventedSymbols]
  )

  const handleNrOfBalls = useCallback(
    (value: number) => {
      const nrOfBalls = Math.max(1, Math.min(value, maxNrOfBalls))
      setBallsPerDispense(nrOfBalls)
    },
    [maxNrOfBalls, setBallsPerDispense]
  )

  return (
    <div className="">
      <RadioGroup.Root
        className="w-fit-content mb-4 [&_input]:hidden"
        value={String(isSplitUpDispense)}
        onValueChange={(value) => {
          setIsSplitUpDispense(value === 'true')
        }}
      >
        <RadioGroup.Item
          className={cn(
            'justify-start p-0 [&_label]:mt-0 [&_label]:font-semibold',
            !isSplitUpDispense && 'text-text-info hover:text-text-info [&_path]:fill-[#3B82F6]'
          )}
          value="false"
          label={t('sentences.dispenseAllAtOnce')}
          isActive={!isSplitUpDispense}
        />
        <RadioGroup.Item
          className={cn(
            'justify-start p-0 [&_label]:mt-0 [&_label]:font-semibold',
            isSplitUpDispense && 'text-text-info hover:text-text-info [&_path]:fill-[#3B82F6]'
          )}
          value="true"
          label={t('sentences.splitUpInBatches')}
          isActive={isSplitUpDispense}
        />
      </RadioGroup.Root>
      {!isSplitUpDispense && (
        <div className="bg-surface-warning-disabled border-border-stroke-warning rounded-md border p-4">
          <div className="font-medium">{t('sentences.splitUpInBatchesWarningTitle')}</div>
          <div className="text-content-sm">{t('sentences.splitUpInBatchesWarningText')}</div>
        </div>
      )}
      {isSplitUpDispense && (
        <>
          <span className="text-content-sm">{t('sentences.splitUpInTextBatches')}</span>
          <InputBase className="mt-2 w-fit">
            <InputContainer>
              <Input
                type="number"
                value={ballsPerDispense ?? undefined}
                className="text-content-base h-8 w-20 py-1"
                min={1}
                max={maxNrOfBalls}
                onBlur={() => handleNrOfBalls(ballsPerDispense as number)}
                onChange={(e) => handleNrOfBalls(parseInt(e.target.value))}
                onKeyDown={handleKeyDown}
                placeholder="1-999"
              />
            </InputContainer>
          </InputBase>
        </>
      )}
    </div>
  )
}

export default BallDispenseFlowForm
